/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  GoogleReCaptchaProvider,
  GoogleReCaptchaCheckbox,
  useGoogleReCaptcha,
} from '@google-recaptcha/react';
import { useNavigate } from 'react-router';
import { ErrorInfo } from '../../classes/client';

import AccountAccessLayout from '../layout/AccountAccessLayout';

import { useMutation } from '@tanstack/react-query';
import Input from '../form/input';
import GlobalError from '../form/global-error';
import SubmitButton from '../form/submit-button';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import AppConfig from '../../classes/app-config';
import { trackPageView } from '../../utils/analytics';
import Separator from '../common/separator';
import GoogleButton from '../common/google-button';
import FacebookButton from '../common/facebook-button';
import { Link as RouterLink } from 'react-router';
import { recaptchaContainerStyle } from './style';
import { ClientContext } from '../../classes/provider/client-context';
import { logCriticalError } from '../../utils';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import { SEOHead } from '../seo';
import { getCanonicalUrl, getAlternateLanguageUrls } from '../../utils/seo-locale';

export type Model = {
  email: string;
  lastname: string;
  firstname: string;
  password: string;
  recaptcha: string;
  acceptedTerms: boolean;
};

const defaultModel: Model = {
  email: '',
  lastname: '',
  firstname: '',
  password: '',
  recaptcha: '',
  acceptedTerms: true,
};

const RegistrationFormWithCaptcha = () => {
  const { reset, isLoading, instance } = useGoogleReCaptcha();
  const handleCaptchaReset = useCallback(() => {
    if (isLoading || !instance) {
      logCriticalError(
        'reCAPTCHA reset skipped: widget not ready',
        JSON.stringify({
          isLoading,
          hasInstance: Boolean(instance),
          hasReset: typeof reset === 'function',
          locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
        }),
      );
      return;
    }
    try {
      reset?.();
    } catch (err) {
      logCriticalError('reCAPTCHA reset() threw', err);
    }
  }, [reset, isLoading, instance]);
  return <RegistrationForm onCaptchaReset={handleCaptchaReset} />;
};

const RegistrationForm = ({ onCaptchaReset }: { onCaptchaReset?: () => void }) => {
  const [model, setModel] = useState<Model>(defaultModel);
  const [error, setError] = useState<ErrorInfo>();
  const navigate = useNavigate();
  const intl = useIntl();

  const client = useContext(ClientContext);
  const mutation = useMutation<void, ErrorInfo, Model>({
    mutationFn: (model: Model) => client.registerNewUser({ ...model }),
    onSuccess: () => navigate('/c/registration-success'),
    onError: (error) => {
      setError(error);
      onCaptchaReset?.();
    },
  });

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    mutation.mutate(model);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();

    const name = event.target.name;
    const value = event.target.value;
    setModel({ ...model, [name as keyof Model]: value });
  };

  const handleRecaptchaChange = useCallback((token: string) => {
    setModel((prev) => ({ ...prev, recaptcha: token }));
  }, []);

  const handleRecaptchaError = useCallback(() => {
    logCriticalError(
      'reCAPTCHA widget reported an error',
      JSON.stringify({
        locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
        siteKeyConfigured: Boolean(AppConfig.getRecaptcha2SiteKey()),
      }),
    );
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    logCriticalError(
      'reCAPTCHA token expired',
      JSON.stringify({
        locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
      }),
    );
    setModel((prev) => ({ ...prev, recaptcha: '' }));
  }, []);

  const maxFormWidth = 350;

  // Generic OAuth registration handler
  const handleOAuthRegister = (authUrl: string | undefined, providerName: string): void => {
    if (!authUrl) {
      console.error(`${providerName} OAuth callback URL is null`);
      return;
    }
    window.location.href = authUrl;
  };

  return (
    <Grid
      container
      sx={{
        justifyContent: 'center',
      }}
    >
      <Grid
        container
        spacing={0}
        sx={{
          justifyContent: 'center',
          alignItems: 'scretch',
          textAlign: 'center',
        }}
      >
        <Grid
          size={{ md: 5, xs: 12 }}
          sx={{
            justifyContent: 'center',
          }}
        >
          <header>
            <Typography variant="h4" component="h1">
              <FormattedMessage id="registration.title" defaultMessage="Become a member" />
            </Typography>

            <Typography
              sx={{
                marginBottom: '16px',
              }}
            >
              <FormattedMessage
                id="registration.desc"
                defaultMessage="Signing up is free and just take a moment "
              />
            </Typography>
          </header>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {AppConfig.isFacebookOauth2Enabled() && (
              <FacebookButton
                text={intl.formatMessage({
                  id: 'registration.facebook.button',
                  defaultMessage: 'Sign up with Facebook',
                })}
                onClick={() => handleOAuthRegister(AppConfig.getFacebookOauth2Url(), 'Facebook')}
              />
            )}
            {AppConfig.isGoogleOauth2Enabled() && (
              <GoogleButton
                text={intl.formatMessage({
                  id: 'registration.google.button',
                  defaultMessage: 'Sign up with Google',
                })}
                onClick={() => handleOAuthRegister(AppConfig.getGoogleOauth2Url(), 'Google')}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              mt: 2,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <FormattedMessage
              id="common.terms-notice"
              defaultMessage="By continuing, you agree to our <termsLink>Terms of Service</termsLink> and <privacyLink>Privacy Policy</privacyLink>."
              values={{
                termsLink: (chunks: React.ReactNode) => (
                  <Link
                    href="https://www.wisemapping.com/termsofuse.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </Link>
                ),
                privacyLink: (chunks: React.ReactNode) => (
                  <Link
                    href="https://www.wisemapping.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </Typography>
        </Grid>
        <Grid size={{ md: 2, xs: 12 }}>
          <Separator
            responsive={true}
            maxWidth={maxFormWidth}
            text={intl.formatMessage({
              id: 'registration.division',
              defaultMessage: 'or',
            })}
          />
        </Grid>
        <Grid size={{ md: 5, xs: 12 }}>
          <main>
            <FormControl css={{ maxWidth: maxFormWidth }}>
              <form
                onSubmit={handleOnSubmit}
                role="form"
                aria-label={intl.formatMessage({
                  id: 'common.registration-form',
                  defaultMessage: 'Registration form',
                })}
              >
                <GlobalError
                  error={error}
                  title={
                    error
                      ? intl.formatMessage({
                          id: 'registration.error.title',
                          defaultMessage: 'Registration Failed',
                        })
                      : undefined
                  }
                />
                <fieldset>
                  <legend>
                    <FormattedMessage
                      id="registration.form-legend"
                      defaultMessage="Account Information"
                    />
                  </legend>
                  <Input
                    name="email"
                    type="email"
                    onChange={handleOnChange}
                    label={intl.formatMessage({
                      id: 'registration.email',
                      defaultMessage: 'Email',
                    })}
                    autoComplete="email"
                    error={error}
                    required={true}
                  />
                  <Input
                    name="firstname"
                    type="text"
                    onChange={handleOnChange}
                    label={intl.formatMessage({
                      id: 'registration.firstname',
                      defaultMessage: 'First Name',
                    })}
                    autoComplete="given-name"
                    error={error}
                    required={true}
                  />
                  <Input
                    name="lastname"
                    type="text"
                    onChange={handleOnChange}
                    label={intl.formatMessage({
                      id: 'registration.lastname',
                      defaultMessage: 'Last Name',
                    })}
                    autoComplete="family-name"
                    error={error}
                    required={true}
                  />
                  <Input
                    name="password"
                    type="password"
                    onChange={handleOnChange}
                    minLength={8}
                    maxLength={39}
                    label={intl.formatMessage({
                      id: 'registration.password',
                      defaultMessage: 'Password',
                    })}
                    autoComplete="new-password"
                    error={error}
                    required={true}
                  />

                  {AppConfig.isRecaptcha2Enabled() && (
                    <>
                      <div css={recaptchaContainerStyle}>
                        <GoogleReCaptchaCheckbox
                          onChange={handleRecaptchaChange}
                          onError={handleRecaptchaError}
                          onExpired={handleRecaptchaExpired}
                        />
                      </div>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '11px', textAlign: 'center', color: 'text.secondary' }}
                      >
                        <FormattedMessage
                          id="registration.recaptcha-notice"
                          defaultMessage="This site is protected by reCAPTCHA and the Google <privacyLink>Privacy Policy</privacyLink> and <termsLink>Terms of Service</termsLink> apply."
                          values={{
                            privacyLink: (chunks: React.ReactNode) => (
                              <Link
                                href="https://policies.google.com/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {chunks}
                              </Link>
                            ),
                            termsLink: (chunks: React.ReactNode) => (
                              <Link
                                href="https://policies.google.com/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {chunks}
                              </Link>
                            ),
                          }}
                        />
                      </Typography>
                    </>
                  )}
                  <SubmitButton
                    value={intl.formatMessage({
                      id: 'registration.register',
                      defaultMessage: 'Register',
                    })}
                    isLoading={mutation.isPending}
                  />
                </fieldset>
              </form>
            </FormControl>
          </main>
        </Grid>
      </Grid>
      <Grid size={12} sx={{ textAlign: 'center', mt: 2 }}>
        <Link component={RouterLink} to="/c/login">
          <FormattedMessage id="header.haveaccount" defaultMessage="Already have an account?" />
        </Link>
      </Grid>
    </Grid>
  );
};

const RegistationPage = (): React.ReactElement => {
  const intl = useIntl();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = intl.formatMessage({
      id: 'registration.page-title',
      defaultMessage: 'Registration | WiseMapping',
    });
    trackPageView(window.location.pathname, 'Registration:Init');
  }, []);

  // Redirect to login if registration is disabled
  useEffect(() => {
    if (!AppConfig.isRegistrationEnabled()) {
      navigate('/c/login');
    }
  }, [navigate]);

  // Don't render if registration is disabled
  if (!AppConfig.isRegistrationEnabled()) {
    return <></>;
  }

  const canonicalUrl = getCanonicalUrl('/c/registration');
  const alternateLanguages = getAlternateLanguageUrls('/c/registration');
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://app.wisemapping.com';

  return (
    <>
      <SEOHead
        title="Sign Up | WiseMapping"
        description="Create your free WiseMapping account to start creating mind maps, organizing ideas, and collaborating with others. Join thousands of users worldwide."
        keywords="sign up, register, create account, mind mapping, free account, collaboration, brainstorming"
        canonicalUrl={canonicalUrl}
        alternateLanguages={alternateLanguages}
        ogType="website"
        robots="index, follow"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Sign Up - WiseMapping',
          description:
            'Create your free WiseMapping account to start creating mind maps, organizing ideas, and collaborating with others.',
          url: `${baseUrl}${canonicalUrl}`,
          mainEntity: {
            '@type': 'WebApplication',
            name: 'WiseMapping',
            applicationCategory: 'ProductivityApplication',
            operatingSystem: 'Web Browser',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
          },
        }}
      />
      <AccountAccessLayout
        headerType="only-signin"
        contentSx={{
          padding: { xs: '8px 16px', md: '16px 16px' },
        }}
      >
        {(() => {
          if (!AppConfig.isRecaptcha2Enabled()) {
            return <RegistrationForm />;
          }
          const siteKey = AppConfig.getRecaptcha2SiteKey();
          if (!siteKey) {
            logCriticalError(
              'reCAPTCHA enabled but siteKey is missing — provider will not render',
              JSON.stringify({ enabled: true, hasSiteKey: false }),
            );
            return <RegistrationForm />;
          }
          return (
            <GoogleReCaptchaProvider type="v2-checkbox" siteKey={siteKey}>
              <RegistrationFormWithCaptcha />
            </GoogleReCaptchaProvider>
          );
        })()}
      </AccountAccessLayout>
    </>
  );
};

export default RegistationPage;
